import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
import FormatAlignCenterSharpIcon from '@mui/icons-material/FormatAlignCenterSharp';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ECategoryType } from '../../data/Constants';

import type { IconTypeProps } from '../../../types/IComponentTypes';
/**
 * Returns a combined className string for the icon, including disabled and custom classes.
 *
 * @param {string} base - The base class name for the icon.
 * @param {boolean} [disabled] - If true, adds the 'disabled-icon' class.
 * @param {string} [className] - Additional custom class names.
 * @returns {string} The combined class name string.
 */
const getClassName = (base: string, disabled?: boolean, className?: string): string =>
  `${base}${disabled ? ' disabled-icon' : ''}${className ? ' ' + className : ''}`;

/**
 * Renders the appropriate Material UI icon based on the type and props provided.
 *
 * @param {IconTypeProps} props - The props for the icon, including type and optional styling/handlers.
 * @returns {React.ReactElement | null} The rendered icon element, or null if type is not recognized.
 */
function renderIcon(props: IconTypeProps): React.ReactElement | null {
  const { type, onClick, style, disabled, fontSize, className, ...rest } = props;
  switch (type) {
    case ECategoryType.Standard:
      return <FolderIcon className="category-icon" />;
    case ECategoryType.MultiNumeric:
      return <FolderIcon className="category-icon multi-numeric-icon" />;
    case 'delete':
      return <DeleteIcon className="category-icon delete-icon border-icon" />;
    case 'Numeric':
      return <CasinoOutlinedIcon className="category-icon" />;
    case 'Feature':
      return <KeyboardCommandKeyIcon className="category-icon" />;
    case 'String':
      return <FormatAlignCenterSharpIcon className="category-icon" />;
    case 'collapse':
      return <ExpandMoreIcon className="category-icon collapse-icon" />;
    case 'expanded':
      return <ChevronRightIcon className="category-icon collapse-icon" />;
    case 'add':
      return <AddIcon className="category-icon border-icon" />;
    case 'save':
      return (
        <SaveIcon
          className={getClassName('category-icon border-icon', disabled, className)}
          onClick={disabled ? undefined : onClick}
          style={style}
          aria-disabled={disabled}
          fontSize={fontSize}
          {...rest}
        />
      );
    case 'cancel':
      return (
        <CancelIcon
          className={getClassName('category-icon border-icon', disabled, className)}
          onClick={disabled ? undefined : onClick}
          style={style}
          aria-disabled={disabled}
          fontSize={fontSize}
          {...rest}
        />
      );
    case 'edit':
      return (
        <EditIcon
          className={getClassName('category-icon border-icon', disabled, className)}
          onClick={disabled ? undefined : onClick}
          style={style}
          aria-disabled={disabled}
          fontSize={fontSize}
          {...rest}
        />
      );
    default:
      return null;
  }
}

/**
 * Renders a Material UI icon based on the provided type and props.
 * Supports custom styling, click handling, and disabled state.
 *
 * @param {IconTypeProps} props - The props for the icon component.
 * @returns {JSX.Element | null} The rendered icon element or null if type is not recognized.
 */
const IconType = (props: IconTypeProps) => {
  return <>{renderIcon(props)}</>;
};

export default IconType;
